import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './GalaxyView.css';

const TEAL = '#1a96a6';
const RED  = '#DB3A34';
const GOLD = '#FFD700';
const CYAN = '#5ccfdc';
const WARM = '#f2ece0';

const YEARS = [
  {
    year: '2026', era: 'current',
    pctX: 0.68,   pctY: 0.30,
    color: TEAL,
    satellites: [
      { id: 'sentinel',   label: 'Sentinel',      tag: 'ML Pipeline',     path: '/projects/sentinel',   color: TEAL, angle: -110 },
      { id: 'prism',      label: 'Prism',         tag: 'API Gateway',     path: '/projects/prism',       color: TEAL, angle:  -10 },
      { id: 'lens',       label: 'Lens',          tag: 'Semantic Search', path: '/projects/lens',        color: TEAL, angle:   70 },
    ],
  },
  {
    year: '2024', era: 'momentum',
    pctX: 0.51,   pctY: 0.43,
    color: RED,
    satellites: [
      { id: 'flux',       label: 'Flux',          tag: 'Unit Converter',  path: '/projects/flux',        color: TEAL, angle: -130 },
      { id: 'synechron',  label: 'Synechron',     tag: 'Software Dev',    path: null,                    color: RED,  angle:   50 },
    ],
  },
  {
    year: '2023', era: 'growth',
    pctX: 0.35,   pctY: 0.58,
    color: CYAN,
    satellites: [
      { id: 'codecollab', label: 'CodeCollab',    tag: 'Collab Editor',   path: '/projects/codecollab',  color: TEAL, angle: -130 },
      { id: 'etherea',    label: 'Etherea',       tag: 'Frontend Intern', path: null,                    color: RED,  angle:  -30 },
      { id: 'oracle',     label: 'Oracle Cert',   tag: 'Java SE 8',       path: null,                    color: GOLD, angle:   80 },
    ],
  },
  {
    year: '2020', era: 'foundation',
    pctX: 0.20,   pctY: 0.72,
    color: GOLD,
    satellites: [
      { id: 'asu',        label: 'Arizona State', tag: 'B.Sc. CS',        path: null,                    color: GOLD, angle:  -90 },
    ],
  },
];

const EXIT_MS = 680;

function useStarField(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const STAR_COUNT = 260;
    const stars = Array.from({ length: STAR_COUNT }, () => ({
      x:     Math.random(),
      y:     Math.random(),
      r:     Math.random() * 1.3 + 0.3,
      base:  Math.random() * 0.52 + 0.10,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.65 + 0.18,
    }));

    const accents = Array.from({ length: 22 }, () => ({
      x:     Math.random(),
      y:     Math.random(),
      r:     Math.random() * 1.1 + 1.7,
      base:  Math.random() * 0.28 + 0.22,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.28 + 0.08,
    }));

    let shooting = null;
    const scheduleShoot = () => {
      setTimeout(() => {
        shooting = {
          x: Math.random() * 0.5 + 0.08,
          y: Math.random() * 0.38,
          t: 0,
          dur: 52,
        };
        scheduleShoot();
      }, Math.random() * 9000 + 5500);
    };
    scheduleShoot();

    let t   = 0;
    let raf = null;

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        const alpha = Math.max(0, s.base + Math.sin(t * s.speed + s.phase) * 0.14);
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(215,232,238,${alpha})`;
        ctx.fill();
      }

      for (const s of accents) {
        const alpha = Math.max(0, s.base + Math.sin(t * s.speed + s.phase) * 0.11);
        const x = s.x * w;
        const y = s.y * h;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = '#cce8f0';
        ctx.fillRect(x - s.r, y - 0.5, s.r * 2, 1);
        ctx.fillRect(x - 0.5, y - s.r, 1, s.r * 2);
        ctx.globalAlpha = 1;
      }

      if (shooting) {
        const prog  = shooting.t / shooting.dur;
        const DX    = 0.21;
        const DY    = 0.09;
        const cx    = (shooting.x + prog * DX) * w;
        const cy    = (shooting.y + prog * DY) * h;
        const tx    = shooting.x * w;
        const ty    = shooting.y * h;
        const alpha = prog < 0.38 ? prog / 0.38 : (1 - prog) / 0.62;
        const grad  = ctx.createLinearGradient(tx, ty, cx, cy);
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(1, `rgba(255,255,255,${Math.max(0, alpha * 0.85)})`);
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(cx, cy);
        ctx.strokeStyle = grad;
        ctx.lineWidth   = 1.3;
        ctx.stroke();
        shooting.t++;
        if (shooting.t >= shooting.dur) shooting = null;
      }

      t  += 0.011;
      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [canvasRef]);
}

const PERSONAL_STAR = { pctX: 0.25, pctY: 0.28 };

export default function GalaxyView({ onClose, onStartClose }) {
  const navigate  = useNavigate();
  const canvasRef = useRef(null);

  const [visible,         setVisible]         = useState(false);
  const [closing,         setClosing]         = useState(false);
  const [focused,         setFocused]         = useState(null);
  const [hoveredSat,      setHoveredSat]      = useState(null);
  const [hoveredPersonal, setHoveredPersonal] = useState(false);
  const [dims,       setDims]       = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });

  useStarField(canvasRef);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const handler = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const triggerClose = useCallback(() => {
    onStartClose?.();
    setClosing(true);
    setTimeout(onClose, EXIT_MS);
  }, [onClose, onStartClose]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (focused) setFocused(null);
      else         triggerClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [focused, triggerClose]);

  const minDim    = Math.min(dims.w, dims.h);
  const orbitR    = Math.max(110, minDim * 0.185);
  const yearR     = Math.max(20,  minDim * 0.034);
  const personalR = Math.max(18,  minDim * 0.030);
  const satR   = Math.max(12,  minDim * 0.021);

  const yearPx      = useCallback((y) => ({ x: y.pctX * dims.w, y: y.pctY * dims.h }), [dims]);
  const personalCenter = { x: PERSONAL_STAR.pctX * dims.w, y: PERSONAL_STAR.pctY * dims.h };
  const satPx  = useCallback((yearData, sat) => {
    const c   = yearPx(yearData);
    const rad = sat.angle * Math.PI / 180;
    return { x: c.x + Math.cos(rad) * orbitR, y: c.y + Math.sin(rad) * orbitR };
  }, [yearPx, orbitR]);

  const handleBgClick = useCallback(() => {
    setFocused(null);
    setHoveredSat(null);
  }, []);

  const handleYearClick = useCallback((e, yr) => {
    e.stopPropagation();
    setFocused(f => f === yr ? null : yr);
    setHoveredSat(null);
  }, []);

  const handleSatClick = useCallback((e, path, year) => {
    e.stopPropagation();
    if (!path) return;
    navigate(path, { state: { year } });
  }, [navigate]);

  const handleSatEnter = useCallback((id) => setHoveredSat(id),   []);
  const handleSatLeave = useCallback(()   => setHoveredSat(null), []);

  const handlePersonalClick = useCallback((e) => {
    e.stopPropagation();
    triggerClose();
    navigate('/', { state: { openPersonal: true, fromGalaxy: true } });
  }, [triggerClose, navigate]);

  const wrapClass = [
    'galaxy-wrap',
    visible && !closing ? 'galaxy-wrap--in'  : '',
    closing             ? 'galaxy-wrap--out' : '',
  ].filter(Boolean).join(' ');

  const filterMap = {
    [TEAL]: 'url(#gf-teal)',
    [RED]:  'url(#gf-red)',
    [GOLD]: 'url(#gf-gold)',
    [CYAN]: 'url(#gf-cyan)',
  };

  return (
    <div className={wrapClass} onClick={handleBgClick}>

      <canvas ref={canvasRef} className="galaxy-canvas" />

      <svg className="galaxy-nebula" aria-hidden="true">
        <defs>
          <radialGradient id="gneb-teal"   cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#1a96a6" stopOpacity="0.11" />
            <stop offset="100%" stopColor="#1a96a6" stopOpacity="0"    />
          </radialGradient>
          <radialGradient id="gneb-violet" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#5f23c8" stopOpacity="0.07" />
            <stop offset="100%" stopColor="#5f23c8" stopOpacity="0"    />
          </radialGradient>
          <radialGradient id="gneb-gold"   cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#b89000" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#b89000" stopOpacity="0"    />
          </radialGradient>
          <radialGradient id="gneb-red"    cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#DB3A34" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#DB3A34" stopOpacity="0"    />
          </radialGradient>
        </defs>
        <ellipse cx="70%" cy="20%" rx="430" ry="300" fill="url(#gneb-teal)"   />
        <ellipse cx="12%" cy="75%" rx="320" ry="230" fill="url(#gneb-gold)"   />
        <ellipse cx="10%" cy="28%" rx="260" ry="190" fill="url(#gneb-violet)" />
        <ellipse cx="50%" cy="45%" rx="340" ry="240" fill="url(#gneb-red)"    />
      </svg>

      <svg className="galaxy-svg" aria-label="Interactive galaxy timeline">
        <defs>
          {[
            { id: 'gf-teal', dev: 7 },
            { id: 'gf-red',  dev: 7 },
            { id: 'gf-gold', dev: 6 },
            { id: 'gf-cyan', dev: 7 },
          ].map(({ id, dev }) => (
            <filter key={id} id={id} x="-70%" y="-70%" width="240%" height="240%">
              <feGaussianBlur stdDeviation={dev} result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
          <filter id="gf-sat" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="gf-personal" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="10" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Personal identity star */}
        <g
          style={{
            opacity:    focused ? 0.18 : 1,
            transition: 'opacity 0.55s ease',
            cursor:     'pointer',
          }}
          onClick={handlePersonalClick}
          onMouseEnter={() => setHoveredPersonal(true)}
          onMouseLeave={() => setHoveredPersonal(false)}
        >
          {/* Diffuse ambient glow */}
          <circle
            cx={personalCenter.x} cy={personalCenter.y}
            r={personalR * 5.5}
            fill={WARM}
            fillOpacity={hoveredPersonal ? 0.04 : 0.022}
            style={{ transition: 'fill-opacity 0.45s ease', pointerEvents: 'none' }}
          />
          {/* Slow pulse ring */}
          <circle
            cx={personalCenter.x} cy={personalCenter.y}
            r={personalR * 2.8}
            fill="none"
            stroke={WARM}
            className="galaxy-personal-pulse"
            style={{ pointerEvents: 'none' }}
          />
          {/* Inner halo */}
          <circle
            cx={personalCenter.x} cy={personalCenter.y}
            r={personalR * 1.7}
            fill={WARM}
            fillOpacity={hoveredPersonal ? 0.14 : 0.07}
            style={{ transition: 'fill-opacity 0.38s ease', pointerEvents: 'none' }}
          />
          {/* Core */}
          <circle
            cx={personalCenter.x} cy={personalCenter.y}
            r={hoveredPersonal ? personalR * 1.08 : personalR}
            fill={WARM}
            fillOpacity={0.92}
            filter="url(#gf-personal)"
            style={{ transition: 'r 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          />
          {/* Sphere highlight */}
          <circle
            cx={personalCenter.x - personalR * 0.28}
            cy={personalCenter.y - personalR * 0.32}
            r={personalR * 0.22}
            fill="white"
            fillOpacity="0.35"
            style={{ pointerEvents: 'none' }}
          />
          {/* Hover ring */}
          <circle
            cx={personalCenter.x} cy={personalCenter.y}
            r={personalR * 1.6}
            fill="none"
            stroke={WARM}
            strokeWidth="1"
            strokeOpacity={hoveredPersonal ? 0.45 : 0}
            style={{ transition: 'stroke-opacity 0.22s ease', pointerEvents: 'none' }}
          />
        </g>

        {/* Constellation lines */}
        {YEARS.slice(0, -1).map((y, i) => {
          const from = yearPx(y);
          const to   = yearPx(YEARS[i + 1]);
          return (
            <line
              key={`cl-${i}`}
              x1={from.x} y1={from.y}
              x2={to.x}   y2={to.y}
              stroke="rgba(200,225,232,0.18)"
              strokeWidth="1"
              strokeDasharray="5 10"
              style={{ pointerEvents: 'none' }}
            />
          );
        })}

        {/* Year clusters */}
        {YEARS.map((yearData, yi) => {
          const center    = yearPx(yearData);
          const isFocused = focused === yearData.year;
          const isDimmed  = focused !== null && !isFocused;
          const glowFilter = filterMap[yearData.color] || 'url(#gf-teal)';

          return (
            <g
              key={yearData.year}
              style={{ transition: 'opacity 0.55s ease', opacity: isDimmed ? 0.10 : 1 }}
            >
              {/* Orbit ring — always rendered, fades in/out via opacity */}
              <circle
                cx={center.x} cy={center.y}
                r={orbitR}
                fill="none"
                stroke={yearData.color}
                strokeWidth="1"
                strokeDasharray="6 10"
                style={{
                  pointerEvents:  'none',
                  opacity:        isFocused ? 1 : 0,
                  transition:     'opacity 0.5s ease',
                  strokeOpacity:  0.13,
                }}
              />

              {/* Satellites */}
              {yearData.satellites.map((sat, si) => {
                const sp        = satPx(yearData, sat);
                const isHovered = hoveredSat === sat.id;
                const satFilter = filterMap[sat.color] || 'url(#gf-sat)';

                return (
                  <g
                    key={sat.id}
                    className={`galaxy-satellite${isFocused ? ' galaxy-satellite--in' : ''}`}
                    style={{
                      '--sat-i':     si,
                      cursor:        sat.path ? 'pointer' : 'default',
                      pointerEvents: isFocused ? 'all' : 'none',
                    }}
                    onClick={(e)    => handleSatClick(e, sat.path, yearData.year)}
                    onMouseEnter={() => sat.path && handleSatEnter(sat.id)}
                    onMouseLeave={handleSatLeave}
                  >
                    <line
                      x1={center.x} y1={center.y}
                      x2={sp.x}     y2={sp.y}
                      stroke={sat.color}
                      strokeOpacity="0.12"
                      strokeWidth="0.8"
                      style={{ pointerEvents: 'none' }}
                    />

                    <circle
                      cx={sp.x} cy={sp.y}
                      r={satR * 2.0}
                      fill={sat.color}
                      fillOpacity={isHovered ? 0.12 : 0.05}
                      style={{ transition: 'fill-opacity 0.28s', pointerEvents: 'none' }}
                    />

                    <circle
                      cx={sp.x} cy={sp.y}
                      r={satR}
                      fill={sat.color}
                      fillOpacity={isHovered ? 1 : 0.82}
                      filter={satFilter}
                      style={{ transition: 'fill-opacity 0.28s' }}
                    />

                    {sat.path && (
                      <circle
                        cx={sp.x + satR * 0.62}
                        cy={sp.y - satR * 0.62}
                        r={satR * 0.30}
                        fill="#3ecf6e"
                        className="galaxy-sat-live"
                        style={{ pointerEvents: 'none' }}
                      />
                    )}

                    {/* Hover ring — always rendered, opacity transition */}
                    <circle
                      cx={sp.x} cy={sp.y}
                      r={satR * 1.55}
                      fill="none"
                      stroke={sat.color}
                      strokeWidth="1"
                      strokeOpacity={isHovered ? 0.55 : 0}
                      style={{ transition: 'stroke-opacity 0.22s ease', pointerEvents: 'none' }}
                    />
                  </g>
                );
              })}

              {/* Year outer pulse ring */}
              <circle
                cx={center.x} cy={center.y}
                r={yearR * 2.7}
                fill="none"
                stroke={yearData.color}
                className={`galaxy-year-pulse${isFocused ? ' galaxy-year-pulse--focused' : ''}`}
                style={{ animationDelay: `${yi * -0.8}s`, pointerEvents: 'none' }}
              />

              {/* Year halo */}
              <circle
                cx={center.x} cy={center.y}
                r={yearR * 1.65}
                fill={yearData.color}
                fillOpacity={isFocused ? 0.13 : 0.06}
                style={{ transition: 'fill-opacity 0.4s ease', pointerEvents: 'none' }}
              />

              {/* Year core */}
              <circle
                cx={center.x} cy={center.y}
                r={isFocused ? yearR * 1.12 : yearR}
                fill={yearData.color}
                fillOpacity={0.88}
                filter={glowFilter}
                className="galaxy-year-core"
                onClick={(e) => handleYearClick(e, yearData.year)}
                style={{ cursor: 'pointer' }}
              />

              {/* Sphere highlight */}
              <circle
                cx={center.x - yearR * 0.28}
                cy={center.y - yearR * 0.32}
                r={yearR * 0.24}
                fill="white"
                fillOpacity="0.22"
                style={{ pointerEvents: 'none' }}
              />
            </g>
          );
        })}
      </svg>

      {/* DOM label layer */}
      <div className="galaxy-labels" aria-hidden="true">
        {YEARS.map((yearData) => {
          const center    = yearPx(yearData);
          const isFocused = focused === yearData.year;
          const isDimmed  = focused !== null && !isFocused;

          return (
            <React.Fragment key={yearData.year}>
              <div
                className={[
                  'galaxy-year-label',
                  isDimmed  ? 'galaxy-year-label--dim'     : '',
                  isFocused ? 'galaxy-year-label--focused' : '',
                ].filter(Boolean).join(' ')}
                style={{
                  left:  center.x,
                  top:   center.y + (isFocused ? yearR * 1.12 : yearR) + 18,
                  color: yearData.color,
                }}
                onClick={(e) => handleYearClick(e, yearData.year)}
              >
                {yearData.year}
                <span className="galaxy-year-era">{yearData.era}</span>
              </div>

              {yearData.satellites.map((sat, si) => {
                const sp        = satPx(yearData, sat);
                const aboveYear = sp.y < center.y;
                const isHovered = hoveredSat === sat.id;

                return (
                  <div
                    key={sat.id}
                    className={[
                      'galaxy-sat-label',
                      isFocused ? 'galaxy-sat-label--in'      : '',
                      sat.path  ? 'galaxy-sat-label--link'    : '',
                      isHovered ? 'galaxy-sat-label--hovered' : '',
                    ].filter(Boolean).join(' ')}
                    style={{
                      left:      sp.x,
                      top:       aboveYear ? sp.y - satR - 8 : sp.y + satR + 6,
                      transform: aboveYear ? 'translate(-50%, -100%)' : 'translateX(-50%)',
                      '--sat-i': si,
                    }}
                    onClick={(e)    => handleSatClick(e, sat.path, yearData.year)}
                    onMouseEnter={() => sat.path && handleSatEnter(sat.id)}
                    onMouseLeave={handleSatLeave}
                  >
                    <div className="galaxy-sat-name" style={{ color: sat.color }}>
                      {sat.label}
                    </div>
                    <div className="galaxy-sat-tag">{sat.tag}</div>
                    {sat.path && (
                      <div className="galaxy-sat-cta">→ open showcase</div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Personal identity star label — to the right of the star */}
        <div
          className={`galaxy-personal-label${hoveredPersonal ? ' galaxy-personal-label--hovered' : ''}${focused ? ' galaxy-personal-label--dim' : ''}`}
          style={{
            left:      personalCenter.x + personalR + 16,
            top:       personalCenter.y,
            transform: 'translateY(-50%)',
          }}
          onClick={handlePersonalClick}
          onMouseEnter={() => setHoveredPersonal(true)}
          onMouseLeave={() => setHoveredPersonal(false)}
        >
          <div className="galaxy-personal-name">Beyond the Code</div>
          <div className="galaxy-personal-sub">// the person</div>
          <div className="galaxy-personal-cta">→ explore</div>
        </div>
      </div>

      {/* Close button */}
      <button
        className="galaxy-close"
        onClick={(e) => { e.stopPropagation(); triggerClose(); }}
        aria-label="Return to timeline"
      >
        <svg width="11" height="10" viewBox="0 0 11 10" fill="none" aria-hidden="true">
          <path d="M3.5 1L1 5L3.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7.5 1L10 5L7.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Timeline</span>
      </button>

      {/* Hint — key swap triggers fade-in animation on text change */}
      <p
        key={focused ? 'focused' : 'idle'}
        className={`galaxy-hint${focused ? ' galaxy-hint--project' : ''}`}
      >
        {focused
          ? 'click a node to enter its showcase · esc to deselect'
          : 'select a year · or the warm star · esc to close'}
      </p>
    </div>
  );
}
